import { auth } from '@fleetops/firebase'
import { useAuthStore } from '@/store/auth.store'
import { apiFireAndForget, hasApi } from './api'

// Lightweight client-side error reporting — errors are POSTed to the Worker,
// which logs them to Cloudflare Workers Logs ([observability] is enabled in
// wrangler.toml), so production errors are visible in the dashboard /
// `wrangler tail` without any third-party service.

const seen = new Set<string>()
let reportsLeft = 10 // cap per page load — a render loop must not DDoS the Worker

export function reportClientError(payload: {
  message: string
  stack?: string
  source: 'window.onerror' | 'unhandledrejection' | 'error-boundary' | 'firestore-listener'
  componentStack?: string
}) {
  if (import.meta.env.DEV || !hasApi()) return
  const session = useAuthStore.getState().session
  if (!session || !auth.currentUser) return // nothing to attribute; endpoint requires auth
  if (reportsLeft <= 0 || seen.has(payload.message)) return
  seen.add(payload.message)
  reportsLeft--

  // Never throws, never rejects — reporting an error must not cause one.
  apiFireAndForget(`/tenants/${session.tenantId}/client-errors`, {
    method: 'POST',
    body: { ...payload, url: window.location.pathname, userAgent: navigator.userAgent },
  })
}

export function installGlobalErrorReporting() {
  window.addEventListener('error', (e) => {
    const err = e.error as { message?: unknown; stack?: unknown } | undefined
    reportClientError({
      message: String(e.message || err?.message || 'Unknown error'),
      stack: err?.stack ? String(err.stack) : undefined,
      source: 'window.onerror',
    })
  })
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason as { message?: string; stack?: string } | undefined
    reportClientError({
      message: String(reason?.message ?? e.reason ?? 'Unhandled rejection'),
      stack: reason?.stack ? String(reason.stack) : undefined,
      source: 'unhandledrejection',
    })
  })
}
